import React, {useRef} from 'react';
import styled from 'styled-components';
import TypographyHeading from '../components/Typography/TypographyHeading';
import TypographyBody from '../components/Typography/TypographyBody';
import {useIntersection} from 'react-use';
import {useFetchVWCelebrationStatistics} from '../hooks/useFetchVWCelebrationStatistics';
import GradientMeshBackground from '../components/GradientMeshBackground';
import CountUp from 'react-countup';
import {GPSTourBrandExperiences} from '../constants';
import {EventStatistics} from '../types/vwCelebration';

const Container = styled.div`
  width: 100%;
  position: relative;
  padding: 32px;
  overflow: hidden;
  padding-bottom: 48px;
`;

const GridWrapper = styled.div`
  width: 100%;
  display: grid;
  gap: 28px;
  grid-template-columns: repeat(4, 1fr);
  max-width: 66vw;
  margin: 0 auto;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 52px;
    gap: 24px;
  }
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const Heading = styled(TypographyHeading)`
  font-size: 68px;
  @media (max-width: 768px) {
    font-size: 64px;
  }
`;

const Description = styled(TypographyBody)`
  font-size: 22px;
  white-space: nowrap;
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

type EventCounterProps = {
  gpsTourBranch?: GPSTourBrandExperiences;
};

const EventCounter: React.FC<EventCounterProps> = ({
  gpsTourBranch = GPSTourBrandExperiences.ID4,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const {data} = useFetchVWCelebrationStatistics({gpsTourBranch});
  const statisticsConfig = {
    [GPSTourBrandExperiences.ID4]: [
      {
        key: 'days',
        label: 'Days',
      },
      {
        key: 'kilometres',
        label: 'Kilometres Driven',
      },
      {
        key: 'kilometresDrawn',
        label: 'Kilometres Drawn',
      },
      {
        key: 'chargers',
        label: 'Chargers',
      },
    ],
    [GPSTourBrandExperiences.AMAROK]: [
      {
        key: 'days',
        label: 'Days',
      },
      {
        key: 'hours',
        label: 'Hours',
      },
      {
        key: 'kilometres',
        label: 'Kilometres Driven',
      },
      {
        key: 'visitedDeserts',
        label: 'Visited Deserts',
      },
    ],
  };

  const containerIntersection = useIntersection(anchorRef, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });

  return (
    <>
      <div ref={anchorRef} />
      <Container>
        <GridWrapper>
          {statisticsConfig[gpsTourBranch]?.map((item, index) => {
            const field = item.key as keyof EventStatistics;
            const triggerRerender =
              containerIntersection?.intersectionRatio === 0 ? '1' : '2';

            return (
              <Item key={index}>
                <CountUp
                  key={triggerRerender}
                  preserveValue
                  delay={0}
                  start={0}
                  end={data?.[field] || 0}
                >
                  {({countUpRef}) => (
                    <Heading
                      ref={countUpRef as React.RefObject<HTMLDivElement>}
                      bold
                      color="#fff"
                    >
                      {data?.[field] || 0}
                    </Heading>
                  )}
                </CountUp>
                <Description color="#fff">{item.label}</Description>
              </Item>
            );
          })}
        </GridWrapper>

        <GradientMeshBackground />
      </Container>
    </>
  );
};

export default EventCounter;
